import 'react-photo-view/dist/react-photo-view.css';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { Dispatch, Fragment, SetStateAction, useCallback } from 'react';
import { PhotoSlider } from 'react-photo-view';
import { Images } from '@/assets/images';
import { RuntimeConfiguration } from '@/configuration';
import { NftSoldDetail } from '@/lib/providers/NftContextProvider';
import { usePayment } from '@/lib/providers/PaymentProvider';
import { useWalletDetails } from '@/lib/providers/WalletDetailsProvider';
import { truncateAddress } from '@/lib/utils/common.utils';
import { getChainExplorerAddress } from '@/lib/utils/getChainExplorer.utils';
import { useOpenClaimModal } from '@/provider/ClaimModalOpenProvider';
import RenderIf from './RenderIf';
import RenderNFTItem from './RenderNFTItem';
import { getSaleLabel, isButtonDisabled } from '@/lib/utils/getSaleLabel';
import Link from 'next/link';

type Props = {
  priceData?: number | string;
  selectedNft?: NftSoldDetail;
  isSaleStarted: boolean;
  isSaleEnded: boolean;
  endDate?: string;
  isMobile: boolean;
  visible: boolean;
  index: number;
  setIndex: Dispatch<SetStateAction<number>>;
  handlePreviewImage: () => void;
  balanceTokenQuantity?: number;
  isContactGallery: boolean;
  buyersList: string[];
};

const parseDescription = (description?: string, theme?: Theme) => {
  if (!description) return '';

  let formattedDescription = description.replace(/\n/g, '<br />');

  const styles = `
      color: ${theme?.MojitoClaim?.linkColor};
      font-size: 16px;
      font-family: ${theme?.MojitoClaim?.font?.tertiary};
      font-weight: 400;
      line-height: 140%;
      letter-spacing: -0.14px;
      font-style: normal;
      text-decoration: underline;
      cursor: pointer;
    `;

  const urlRegex = /(https?:\/\/[^\s]+)/g;

  formattedDescription = formattedDescription.replace(urlRegex, (url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="${styles}">${url}</a>`;
  });

  return formattedDescription;
};

const NftRightDetails: React.FC<Props> = ({
  priceData,
  selectedNft: nftDetails,
  isSaleStarted,
  isSaleEnded,
  isMobile,
  index,
  setIndex,
  visible,
  handlePreviewImage,
  balanceTokenQuantity,
  isContactGallery,
  buyersList
}) => {
  const theme = useTheme();
  const { setOpenClaimModal } = useOpenClaimModal();
  const { walletDetails } = useWalletDetails();
  const { setValidation, setError, setPriceInEth, onCheckTokenGatingUsingContract } = usePayment();

  const handleOwner = useCallback(() => {
    if (buyersList?.[0]) {
      window.open(
        getChainExplorerAddress(+RuntimeConfiguration.CHAIN_ID, buyersList?.[0]),
        '_blank',
        'noopener,noreferrer'
      );
    }
  }, [buyersList]);

  const onClickClaim = useCallback(async () => {
    if (isContactGallery) {
      window.open('https://kinetek.art/', '_blank', 'noopener,noreferrer');

      return;
    }
    setOpenClaimModal(true);
    setValidation('clear');
    setError('');
    setPriceInEth(nftDetails?.price.toString() ?? '0');

    if (walletDetails && walletDetails?.provider) {
      onCheckTokenGatingUsingContract({
        address: walletDetails?.address,
        networkDetails: walletDetails?.networkDetails,
        provider: walletDetails?.provider,
        providerType: walletDetails?.providerType,
        balance: walletDetails?.balance,
        refetchBalance: false
      });
    }
  }, [
    isContactGallery,
    setOpenClaimModal,
    setValidation,
    setError,
    setPriceInEth,
    nftDetails?.price,
    walletDetails,
    onCheckTokenGatingUsingContract
  ]);

  return (
    <Fragment>
      {isMobile && (
        <Stack
          mb={4}
          height={'528px'}
          alignItems="center"
          justifyContent="center"
          bgcolor="#1A1A1A"
          borderRadius="12px"
          position="relative"
          overflow="hidden"
        >
          <PhotoSlider
            images={[{ src: nftDetails?.image || '', key: nftDetails?.image || '' }]}
            visible={visible}
            onClose={handlePreviewImage}
            index={index}
            onIndexChange={setIndex}
          />
          {nftDetails?.animation_url ? (
            <RenderNFTItem item={nftDetails?.animation_url} imageUrl={nftDetails.image} onClick={handlePreviewImage} />
          ) : (
            <RenderNFTItem item={nftDetails?.image || Images.CARD_IMG} onClick={handlePreviewImage} />
          )}
        </Stack>
      )}
      <Box mb={3}>
        <Typography
          variant="subtitle1"
          color="text.primary"
          sx={{
            fontFamily: theme?.MojitoClaim?.font?.secondary,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: 'normal'
          }}
          mb={1}
          align="left"
        >
          {nftDetails?.collection_Name}
        </Typography>
        <Typography
          variant="h5"
          fontSize="48px"
          lineHeight="120%"
          color="text.primary"
          textAlign="left"
          fontWeight={500}
          mb={2}
        >
          {nftDetails?.title}
        </Typography>
        <RenderIf condition={!!nftDetails?.owner && !!buyersList?.[0]}>
          <Stack direction="row" alignItems="end">
            <Typography sx={{ color: theme?.MojitoClaim?.delivery?.textColor, fontSize: '14px' }} mr={1}>
              Owner:
            </Typography>
            <Typography
              color="white"
              sx={{
                fontFamily: theme?.MojitoClaim?.font?.tertiary,
                color: theme?.MojitoClaim?.delivery?.textColor,
                lineHeight: '140%',
                letterSpacing: '-0.14px',
                fontSize: '14px',
                fontStyle: 'normal',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
              onClick={handleOwner}
            >
              [
              {truncateAddress(
                (balanceTokenQuantity ?? 0) < (nftDetails?.quantity ?? 0) ? buyersList[0] : nftDetails?.owner
              )}
              ]
            </Typography>
          </Stack>
        </RenderIf>
      </Box>

      <Box borderTop="1px solid #54585F" mb={3}>
        {!nftDetails?.isSoldOut && (
          <Fragment>
            <Typography
              mt={3}
              color="text.primary"
              fontFamily={theme?.MojitoClaim?.font?.secondary}
              textAlign="left"
              fontWeight={500}
            >
              List Price
            </Typography>
            <Stack direction="row" alignItems="baseline">
              <Typography
                variant="h5"
                fontSize="38px"
                fontFamily={theme?.MojitoClaim?.font?.secondary}
                color="text.primary"
                fontWeight={500}
              >
                {Number(nftDetails?.price).toFixed(4)} ETH
              </Typography>
              <Typography
                ml={3}
                fontSize="16px"
                fontFamily={theme?.MojitoClaim?.font?.secondary}
                color={theme.ClaimTemplate?.collectionHeaderColor}
              >
                {priceData ? (Number(nftDetails?.price) * Number(priceData)).toFixed(2) : '-'} USD
              </Typography>
            </Stack>
          </Fragment>
        )}
        <Stack spacing={2} mt={2}>
          {!nftDetails?.isSoldOut && nftDetails?.token_type === 'ERC1155' && (
            <Box
              sx={{
                padding: '4px 8px',
                borderRadius: '100px',
                backgroundColor: '#353535',
                width: 'fit-content',
                letterSpacing: '0.30px'
              }}
            >
              <Typography
                sx={{
                  textAlign: 'left',
                  fontFamily: theme?.MojitoClaim?.font?.secondary,
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  color: theme?.palette?.text?.primary,
                  textDecoration: 'uppercase'
                }}
              >
                {balanceTokenQuantity ?? 0} OUT OF {nftDetails?.quantity} AVAILABLE
              </Typography>
            </Box>
          )}
          <RenderIf condition={!!nftDetails?.isSoldOut}>
            <Typography
              color="text.primary"
              sx={{
                marginTop: '24px',
                textAlign: 'left',
                fontFamily: theme?.MojitoClaim?.font?.secondary,
                fontSize: '32px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '120%'
              }}
            >
              SOLD
            </Typography>
            <RenderIf.Else>
              <Button
                variant="contained"
                fullWidth
                disabled={isButtonDisabled({ isContactGallery, isSaleEnded, isSaleStarted }) || !priceData}
                onClick={onClickClaim}
                sx={{
                  textTransform: 'none',
                  color: '#000',
                  fontFamily: theme?.MojitoClaim?.font?.secondary,
                  backgroundColor: '#fff',
                  width: isMobile ? '100%' : '200px',
                  height: '48px',
                  fontSize: '16px',
                  padding: '16px',
                  cursor: 'pointer',
                  '&:disabled': {
                    color: theme.palette.background.paper,
                    backgroundColor: '#8C939E',
                    cursor: 'default'
                  }
                }}
              >
                {getSaleLabel({ isContactGallery, isSaleEnded, isSaleStarted })}
              </Button>
            </RenderIf.Else>
          </RenderIf>
        </Stack>
      </Box>

      <Box borderTop="1px solid #54585F" mb={3}>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" />}
            aria-controls="panel-content"
            id="panel-header"
            sx={{ display: 'flex', alignItems: 'center', padding: 0, fontFamily: theme?.MojitoClaim?.font?.secondary }}
          >
            Description
          </AccordionSummary>
          <AccordionDetails sx={{ textAlign: 'left', padding: 0, color: theme?.MojitoClaim?.delivery?.textColor }}>
            <Typography fontFamily={theme?.MojitoClaim?.font?.secondary}>
              <span
                dangerouslySetInnerHTML={{
                  __html: parseDescription(nftDetails?.description, theme)
                }}
              />
            </Typography>
          </AccordionDetails>
          <AccordionActions
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mt: 1.5,
              padding: 0
            }}
          />
        </Accordion>
      </Box>
      <Box borderTop="1px solid #54585F" mb={3}>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" />}
            aria-controls="panel-content"
            id="panel-header"
            sx={{ display: 'flex', alignItems: 'center', padding: 0, fontFamily: theme?.MojitoClaim?.font?.secondary }}
          >
            Provenance
          </AccordionSummary>
          <AccordionDetails sx={{ textAlign: 'left', padding: 0, color: theme?.MojitoClaim?.delivery?.textColor }}>
            <Typography fontFamily={theme?.MojitoClaim?.font?.secondary}>
              Minted by{' '}
              <Link
                style={{
                  color: theme?.MojitoClaim?.delivery?.textColor
                }}
                href={getChainExplorerAddress(+RuntimeConfiguration.CHAIN_ID, nftDetails?.owner!)}
                target="_blank"
              >
                {truncateAddress(nftDetails?.owner)}
              </Link>{' '}
              [Kinetek Art Wallet] - {moment(nftDetails?.creation_date, 'DD-MM-YYYY').format('MMM D, YYYY h:mm A')} your
              time
            </Typography>
          </AccordionDetails>
          <AccordionActions
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              mt: 1.5,
              padding: 0
            }}
          />
        </Accordion>
      </Box>
    </Fragment>
  );
};

export default NftRightDetails;
